// frontend/src/components/SucursalChips.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Selector visual de sucursal para el Dashboard.
// Solo visible para admins (dueños). Los cajeros ven su sucursal directamente.
//
// Uso:
//   <SucursalChips
//     sucursalActiva={sucursalFiltro}   // null = "Todas"
//     onChange={(id) => setSucursalFiltro(id)}
//   />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { sucursalesAPI } from '../services/api';
import { isAdmin } from '../hooks/useSucursal';
import './SucursalChips.css';

const SucursalChips = ({ sucursalActiva, onChange }) => {
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading]       = useState(true);

    useEffect(() => {
        if (!isAdmin()) { setLoading(false); return; }
        sucursalesAPI.getAll()
            .then(res => setSucursales(res.data?.data?.sucursales || []))
            .catch(() => setSucursales([]))
            .finally(() => setLoading(false));
    }, []);

    // Si no es admin o no hay sucursales, no renderizar nada
    if (!isAdmin()) return null;
    if (!loading && sucursales.length === 0) return null;

    const getActiveClass = (suc) => {
        if (!suc) return '';
        return suc.es_offline ? 'active-offline' : 'active-online';
    };

    return (
        <div className="sucursal-chips-wrapper">
            <span className="sucursal-chips-label">Vista por sucursal</span>
            <div className="sucursal-chips">

                {/* Skeleton mientras carga */}
                {loading && [1, 2, 3].map(i => (
                    <div key={i} className="sucursal-chip-skeleton" />
                ))}

                {/* Chip "Todas" */}
                {!loading && (
                    <button
                        className={`sucursal-chip ${sucursalActiva === null ? 'active-all' : ''}`}
                        onClick={() => onChange(null)}
                        id="chip-todas-sucursales"
                        title="Ver estadísticas de todas las sucursales"
                    >
                        <span className="chip-dot all" />
                        🌐 Todas las sucursales
                    </button>
                )}

                {/* Chip por sucursal */}
                {!loading && sucursales.map(suc => {
                    const isActive = sucursalActiva === suc.id;
                    return (
                        <button
                            key={suc.id}
                            className={`sucursal-chip ${isActive ? getActiveClass(suc) : ''}`}
                            onClick={() => onChange(suc.id)}
                            id={`chip-sucursal-${suc.id}`}
                            title={`Ver estadísticas de ${suc.nombre}`}
                        >
                            <span className={`chip-dot ${suc.es_offline ? 'offline' : 'online'}`} />
                            <span className="chip-icon">{suc.es_offline ? '📴' : '🏪'}</span>
                            {suc.nombre}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default SucursalChips;
